import React, { useMemo } from "react";
import {
  Check,
  Clock3,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import "./ServicesCatalog.scss";
import { photoVariant } from "../../../../utils/imageUrl";

const ACTIVE_BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "reschedule_requested",
  "reschedule_proposed",
];

function isActiveBookingStatus(status) {
  return ACTIVE_BOOKING_STATUSES.includes(
    String(status || "")
  );
}

function getServiceId(service) {
  return String(
    service?.listingId ||
      service?.id ||
      ""
  );
}

function getBookingServiceId(booking) {
  return String(
    booking?.serviceId ||
      booking?.listingId ||
      ""
  );
}

function getBookingButtonMeta(booking) {
  const status = String(booking?.status || "");

  if (status === "confirmed") {
    return {
      label: "Записано",
      className: "is-confirmed",
      icon: <Check size={15} strokeWidth={2.8} />,
    };
  }

  if (status === "pending") {
    return {
      label: "В очікуванні",
      className: "is-pending",
      icon: <Clock3 size={15} strokeWidth={2.6} />,
    };
  }

  if (status === "reschedule_requested") {
    return {
      label: "Перенесення",
      className: "is-reschedule-requested",
      icon: <RefreshCw size={15} strokeWidth={2.5} />,
    };
  }

  if (status === "reschedule_proposed") {
    return {
      label: "Новий час",
      className: "is-reschedule-proposed",
      icon: <RefreshCw size={15} strokeWidth={2.5} />,
    };
  }

  return {
    label: "Запис",
    className: "is-default",
    icon: null,
  };
}

export default function ServicesCatalog({
  services = [],
  onAddService,
  ownerId,
  clientBookings = [],
  onBookService,
  onOpenBooking,
}) {
  const navigate = useNavigate();

  // Hidden items are only actionable from the full management list — this
  // is just a quick teaser rail, so keep it limited to what's visible now.
  const visibleServices = Array.isArray(services)
    ? services.filter((s) => (s.status || "active") !== "hidden")
    : [];

  const hasServices = visibleServices.length > 0;

  const previewServices = hasServices
    ? visibleServices.slice(0, 5)
    : [];

  const activeBookingsByService = useMemo(() => {
    const map = new Map();

    const activeBookings = Array.isArray(
      clientBookings
    )
      ? clientBookings.filter((booking) =>
          isActiveBookingStatus(booking?.status)
        )
      : [];

    for (const booking of activeBookings) {
      const serviceId =
        getBookingServiceId(booking);

      if (!serviceId) continue;

      if (!map.has(serviceId)) {
        map.set(serviceId, booking);
      }
    }

    return map;
  }, [clientBookings]);

  const openAllServices = () => {
    if (ownerId) {
      navigate(
        `/profile/${encodeURIComponent(
          ownerId
        )}/services`
      );

      return;
    }

    navigate("/business/services");
  };

  const openServiceListing = (item) => {
    const serviceId = getServiceId(item);

    if (!serviceId) return;

    navigate(
      `/listing/${encodeURIComponent(
        serviceId
      )}`
    );
  };

  const formatPrice = (item) => {
    if (Number(item?.price || 0) <= 0) {
      return "Ціну уточнюйте";
    }

    return `${item.price} ${
      item.currency || "ГРН"
    }`;
  };

  const handleServiceAction = (
    event,
    item,
    serviceBooking
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      serviceBooking &&
      typeof onOpenBooking === "function"
    ) {
      onOpenBooking(serviceBooking, item);
      return;
    }

    if (
      !serviceBooking &&
      typeof onBookService === "function"
    ) {
      onBookService(item);
      return;
    }

    openServiceListing(item);
  };

  return (
    <div className="bpServicesCatalog">
      {!hasServices ? (
        <div className="bpServicesCatalog__empty">
          <div className="bpServicesCatalog__emptyIcon">
            <Sparkles
              size={30}
              strokeWidth={2.4}
            />
          </div>

          <strong>
            Каталог послуг порожній
          </strong>

          <p>
            Додай першу послугу, щоб вона
            зʼявилась у бізнес-профілі.
          </p>

          {typeof onAddService === "function" && (
            <button
              type="button"
              onClick={onAddService}
            >
              <Plus
                size={19}
                strokeWidth={2.8}
              />

              Додати послугу
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="bpServicesCatalog__top">
            <div>
              <strong>
                {services.length} послуг
              </strong>

              <span>
                Перші послуги у превʼю блоку
              </span>
            </div>

            <button
              type="button"
              onClick={openAllServices}
            >
              Всі послуги
            </button>
          </div>

          <div className="bpServicesCatalog__grid">
            {previewServices.map((item) => {
              const serviceId =
                getServiceId(item);

              const photo =
                item?.mainPhoto ||
                item?.photos?.[0] ||
                "";

              const serviceBooking =
                activeBookingsByService.get(
                  serviceId
                ) || null;

              const buttonMeta =
                getBookingButtonMeta(
                  serviceBooking
                );

              const hasBookingActions =
                typeof onBookService ===
                  "function" ||
                typeof onOpenBooking ===
                  "function";

              return (
                <article
                  key={serviceId}
                  className={[
                    "bpServiceCard",
                    serviceBooking
                      ? "has-booking"
                      : "",
                    serviceBooking?.status
                      ? `has-${serviceBooking.status}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() =>
                    openServiceListing(item)
                  }
                >
                  <div className="bpServiceCard__image">
                    {photo ? (
                      <img
                        src={photoVariant(photo, "sm")}
                        alt={
                          item?.title ||
                          "Послуга"
                        }
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>🧾</span>
                    )}

                    {serviceBooking && (
                      <div
                        className={[
                          "bpServiceCard__statusBadge",
                          buttonMeta.className,
                        ].join(" ")}
                      >
                        {buttonMeta.icon}
                        {buttonMeta.label}
                      </div>
                    )}
                  </div>

                  <div className="bpServiceCard__body">
                    <strong>
                      {item?.title ||
                        "Нова послуга"}
                    </strong>

                    <em>
                      {item?.duration ||
                        "Тривалість не вказана"}
                    </em>

                    <div className="bpServiceCard__footer">
                      <b>
                        {formatPrice(item)}
                      </b>

                      <button
                        type="button"
                        className={[
                          "bpServiceBookButton",
                          hasBookingActions
                            ? buttonMeta.className
                            : "is-open",
                        ].join(" ")}
                        onClick={(event) =>
                          handleServiceAction(
                            event,
                            item,
                            serviceBooking
                          )
                        }
                      >
                        {hasBookingActions &&
                          buttonMeta.icon}

                        {hasBookingActions
                          ? buttonMeta.label
                          : "Відкрити"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}

            {typeof onAddService ===
              "function" && (
              <button
                type="button"
                className="bpServiceAddCard"
                onClick={onAddService}
              >
                <span>
                  <Plus
                    size={23}
                    strokeWidth={2.8}
                  />
                </span>

                <strong>
                  Додати послугу
                </strong>

                <em>
                  Фото, ціна, тривалість
                </em>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}